import React, { forwardRef, useEffect, useRef, MutableRefObject } from 'react';
import {
  useTable,
  useRowSelect,
  useExpanded,
  Column,
  Row,
  HeaderGroup,
} from 'react-table';

import { Checkbox } from '../Checkbox';

export interface SelectableRowsProp {
  selectableRows?: (selectedRows: Row[], totalSelectedRows: number) => void;
}

export interface UseTableFeaturesProps extends SelectableRowsProp {
  configuration: Column[];
  data: any[];
  isExpandable: boolean;
}

interface UseTableFeaturesReturnProps {
  getTableProps: () => [];
  getTableBodyProps: () => [];
  headerGroups: HeaderGroup[];
  rows: Row[];
  prepareRow: (row: Row) => void;
  visibleColumns: Column[];
}

interface CheckBoxProps {
  indeterminate: boolean;
}

export const useTableFeatures = ({
  selectableRows,
  configuration,
  data,
  isExpandable,
}: UseTableFeaturesProps): UseTableFeaturesReturnProps => {
  const IndeterminateCheckbox = forwardRef(
    ({ indeterminate, ...rest }: CheckBoxProps, ref) => {
      const defaultRef = useRef<HTMLInputElement>(null);
      const resolvedRef =
        (ref as MutableRefObject<HTMLInputElement>) || defaultRef;

      useEffect(() => {
        if (resolvedRef?.current) {
          resolvedRef.current.indeterminate = indeterminate;
        }
      }, [resolvedRef, indeterminate]);

      return <Checkbox type="checkbox" selectedRef={resolvedRef} {...rest} />;
    },
  );

  const handleRowSelection = (hooks) => {
    hooks.visibleColumns.push((columns) => [
      {
        id: 'selection',
        Header: ({ getToggleAllRowsSelectedProps }) => (
          <IndeterminateCheckbox {...getToggleAllRowsSelectedProps()} />
        ),
        Cell: ({ row }) => (
          <IndeterminateCheckbox {...row.getToggleRowSelectedProps()} />
        ),
      },
      ...columns,
    ]);
  };

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
    selectedFlatRows,
    state: { selectedRowIds },
    visibleColumns,
  } = useTable(
    { columns: configuration ?? [], data: data ?? [] },
    ...[
      selectableRows && useRowSelect,
      selectableRows && handleRowSelection,
      isExpandable && useExpanded,
    ].filter((hook) => !!hook),
  );

  useEffect(() => {
    if (selectableRows) {
      const totalSelectedRows = Object.keys(selectedRowIds).length;
      const selectedRows = selectedFlatRows.map(({ original }) => original);
      selectableRows(selectedRows, totalSelectedRows);
    }
  }, [selectableRows, selectedFlatRows, selectedRowIds]);

  return {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
    visibleColumns,
  };
};

// Got to move these functions to theme post theme implementation
export const getRowBackgroundColor = (type, theme) => {
  switch (type) {
    case 'primary':
      return theme?.colors?.lightGreen;
    case 'secondary':
      return theme?.colors?.lightRed;
    default:
      return 'white';
  }
};

export const getRowBorderColor = (type, theme) => {
  switch (type) {
    case 'primary':
      return theme?.colors?.darkGreen;
    case 'secondary':
      return theme?.colors?.darkRed;
    default:
      return 'white';
  }
};
